import React, { useState, useEffect } from 'react';
import { Table, Avatar, Group, Text, ScrollArea } from '@mantine/core';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getVideos, getShorts, getChannels } from '../../services/api';
import classes from './VideoTable.module.css';

interface Video {
    videoTitle: string;
    channel: string;
    channelAvatar: string;
    views: string;
    totalViews: string;
}

interface Channel {
    channelName: string;
    channelAvatar: string;
    subscribers: string;
    totalVideos: string;
}

function decodeHtmlEntities(text: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
}

export function VideoTable() {
    const [videos, setVideos] = useState<Video[]>([]);
    const [shorts, setShorts] = useState<Video[]>([]);
    const [channels, setChannels] = useState<Channel[]>([]);
    const [expandedRows, setExpandedRows] = useState<number[]>([]);
    const [activeTab, setActiveTab] = useState('videos');

    const truncateTitle = (title: string) => {
        if (title.length > 60) {
            return title.slice(0, 60) + '...';
        }
        return title;
    };

    const toggleRow = (index: number) => {
        setExpandedRows(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    useEffect(() => {
        async function fetchData() {
            try {
                const videosResponse = await getVideos();
                const data = videosResponse.data;

                // Sort by views
                const sortedByViews = [...data].sort((a, b) => {
                    const aViews = parseInt(a.views.replace(/\D/g, ''), 10);
                    const bViews = parseInt(b.views.replace(/\D/g, ''), 10);
                    return bViews - aViews;
                });

                setVideos(sortedByViews);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        }

        async function fetchChannels() {
            try {
                const channelsResponse = await getChannels();
                setChannels(channelsResponse.data);
            } catch (error) {
                console.error('Error fetching channels:', error);
            }
        }

        async function fetchShorts() {
            try {
                const shortsResponse = await getShorts();
                setShorts(shortsResponse.data);
            } catch (error) {
                console.error('Error fetching shorts:', error);
            }
        }

        fetchData();
        fetchChannels();
        fetchShorts();
    }, []);

    const displayedVideos = videos.slice(0, 100);

    const renderVideoTable = () => (
        <ScrollArea>
            <Table horizontalSpacing="md" verticalSpacing="xs" className={classes.responsiveTable}>
                <Table.Thead className={classes.th}>
                    <Table.Tr>
                        <Table.Th className={classes.rankColumn}>Rank</Table.Th>
                        <Table.Th>Channel</Table.Th>
                        <Table.Th className={classes.desktopOnly}>Video</Table.Th>
                        <Table.Th className={classes.viewsColumn}>Views 24h</Table.Th>
                        <Table.Th className={classes.viewsColumn}>Total Views</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {displayedVideos.map((video, index) => (
                        <React.Fragment key={index}>
                            <Table.Tr
                                className={classes.tableRow}
                                onClick={() => toggleRow(index)}
                            >
                                <Table.Td className={classes.rankColumn}>{index + 1}</Table.Td>
                                <Table.Td>
                                    <Group gap="sm" className={classes.channelGroup}>
                                        <Avatar size={30} src={video.channelAvatar} radius="xl" />
                                        <Text size="sm" className={classes.channelName}>{decodeHtmlEntities(video.channel)}</Text>
                                        <div className={classes.mobileOnly}>
                                            {expandedRows.includes(index) ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                        </div>
                                    </Group>
                                </Table.Td>
                                <Table.Td className={classes.desktopOnly}>
                                    <Text size="sm" fw={500}>
                                        {truncateTitle(decodeHtmlEntities(video.videoTitle))}
                                    </Text>
                                </Table.Td>
                                <Table.Td className={classes.viewsColumn}>
                                    <Text size="sm" fw={700} className={classes.views}>
                                        {parseInt(video.views.replace(/\D/g, ''), 10).toLocaleString('it-IT')}
                                    </Text>
                                </Table.Td>
                                <Table.Td className={classes.viewsColumn}>
                                    <Text size="sm" fw={700} className={classes.views24h}>
                                        {parseInt(video.totalViews.replace(/\D/g, ''), 10).toLocaleString('it-IT')}
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                            {expandedRows.includes(index) && (
                                <Table.Tr className={classes.mobileExpandedRow}>
                                    <Table.Td colSpan={4}>
                                        <Text size="sm" fw={500}>
                                            {video.videoTitle}
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                        </React.Fragment>
                    ))}
                </Table.Tbody>
            </Table>
        </ScrollArea>
    );

    const renderShortsTable = () => (
        <ScrollArea>
            <Table horizontalSpacing="md" verticalSpacing="xs" className={classes.responsiveTable}>
                <Table.Thead className={classes.th}>
                    <Table.Tr>
                        <Table.Th className={classes.rankColumn}>Rank</Table.Th>
                        <Table.Th>Channel</Table.Th>
                        <Table.Th className={classes.desktopOnly}>Video</Table.Th>
                        <Table.Th className={classes.viewsColumn}>Views 24h</Table.Th>
                        <Table.Th className={classes.viewsColumn}>Total Views</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {shorts.map((video, index) => (
                        <React.Fragment key={index}>
                            <Table.Tr
                                className={classes.tableRow}
                                onClick={() => toggleRow(index)}
                            >
                                <Table.Td className={classes.rankColumn}>{index + 1}</Table.Td>
                                <Table.Td>
                                    <Group gap="sm" className={classes.channelGroup}>
                                        <Avatar size={30} src={video.channelAvatar} radius="xl" />
                                        <Text size="sm" className={classes.channelName}>{decodeHtmlEntities(video.channel)}</Text>
                                        <div className={classes.mobileOnly}>
                                            {expandedRows.includes(index) ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                        </div>
                                    </Group>
                                </Table.Td>
                                <Table.Td className={classes.desktopOnly}>
                                    <Text size="sm" fw={500}>
                                        {truncateTitle(decodeHtmlEntities(video.videoTitle))}
                                    </Text>
                                </Table.Td>
                                <Table.Td className={classes.viewsColumn}>
                                    <Text size="sm" fw={700} className={classes.views}>
                                        {parseInt(video.views.replace(/\D/g, ''), 10).toLocaleString('it-IT')}
                                    </Text>
                                </Table.Td>
                                <Table.Td className={classes.viewsColumn}>
                                    <Text size="sm" fw={700} className={classes.views24h}>
                                        {parseInt(video.totalViews.replace(/\D/g, ''), 10).toLocaleString('it-IT')}
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                            {expandedRows.includes(index) && (
                                <Table.Tr className={classes.mobileExpandedRow}>
                                    <Table.Td colSpan={4}>
                                        <Text size="sm" fw={500}>
                                            {video.videoTitle}
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                        </React.Fragment>
                    ))}
                </Table.Tbody>
            </Table>
        </ScrollArea>
    );

    const renderChannelTable = () => (
        <ScrollArea>
            <Table horizontalSpacing="md" verticalSpacing="xs" className={classes.responsiveTable}>
                <Table.Thead className={classes.th}>
                    <Table.Tr>
                        <Table.Th className={classes.rankColumn}>Rank</Table.Th>
                        <Table.Th>Channel</Table.Th>
                        <Table.Th className={classes.viewsColumn}>Subscribers</Table.Th>
                        <Table.Th className={classes.viewsColumn}>Videos</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {channels.map((channel, index) => (
                        <Table.Tr key={index} className={classes.tableRow}>
                            <Table.Td className={classes.rankColumn}>{index + 1}</Table.Td>
                            <Table.Td>
                                <Group gap="sm" className={classes.channelGroup}>
                                    <Avatar size={30} src={channel.channelAvatar} radius="xl" />
                                    <Text size="sm" className={classes.channelName}>{decodeHtmlEntities(channel.channelName)}</Text>
                                </Group>
                            </Table.Td>
                            <Table.Td className={classes.viewsColumn}>
                                <Text size="sm" fw={700} className={classes.views}>
                                    {channel.subscribers ? parseInt(channel.subscribers.replace(/\D/g, ''), 10).toLocaleString('it-IT') : 'N/A'}
                                </Text>
                            </Table.Td>
                            <Table.Td className={classes.viewsColumn}>
                                <Text size="sm" fw={700} className={classes.views}>
                                    {channel.totalVideos ? parseInt(channel.totalVideos.replace(/\D/g, ''), 10).toLocaleString('it-IT') : 'N/A'}
                                </Text>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </ScrollArea>
    );

    return (
        <>
            <div className={classes.tabsContainer}>
                <button
                    className={`${classes.tabButton} ${activeTab === 'videos' ? classes.active : ''}`}
                    onClick={() => setActiveTab('videos')}
                >
                    Top 100 Videos
                </button>
                <button
                    className={`${classes.tabButton} ${activeTab === 'shorts' ? classes.active : ''}`}
                    onClick={() => setActiveTab('shorts')}
                >
                    Top 100 Shorts
                </button>
                <button
                    className={`${classes.tabButton} ${activeTab === 'channels' ? classes.active : ''}`}
                    onClick={() => setActiveTab('channels')}
                >
                    Top 100 Channels
                </button>
            </div>
            {activeTab === 'videos' && renderVideoTable()}
            {activeTab === 'shorts' && renderShortsTable()}
            {activeTab === 'channels' && renderChannelTable()}
        </>
    );
}
